import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router"
import {
  useSaleCreateStore,
  SaleCreateData,
} from "../stores/useSaleCreateStore"
import { fetchSale } from "../services/salesApi"
import { Sale } from "../types"
import { SaleForm } from "../components/SaleForm"
import BirddogLayout from "../components/BirddogLayout"

// Convert Sale data to SaleCreateData format
const saleToSaleCreateData = (sale: Sale): SaleCreateData => {
  return {
    name: sale.name,
    location_text: sale.location_text,
    description: sale.description || "",
    sale_type: sale.sale_type,
    boundary_geom: sale.boundary_geom,
    timber_products: sale.timber_products,
    inventory_description: sale.inventory_description || "",
    bidding_tc: sale.bidding_tc || "",
    contract_tc: sale.contract_tc || "",
    bid_start_datetime: sale.bid_start_datetime,
    bid_stop_datetime: sale.bid_stop_datetime,
    bid_select_datetime: sale.bid_select_datetime,
  }
}

export const SaleEdit = () => {
  const { saleId } = useParams<{ saleId: string }>()
  const navigate = useNavigate()
  const { initializeForEdit } = useSaleCreateStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>("")

  useEffect(() => {
    const loadSaleData = async () => {
      if (!saleId) {
        setError("No sale ID provided")
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError("")

        const sale = await fetchSale(Number(saleId))
        const saleCreateData = saleToSaleCreateData(sale)

        // Initialize the store for edit mode
        initializeForEdit(Number(saleId), saleCreateData)
      } catch (err) {
        console.error("Failed to load sale for editing:", err)
        setError(
          err instanceof Error ? err.message : "Failed to load sale data"
        )
      } finally {
        setLoading(false)
      }
    }

    void loadSaleData()
  }, [saleId, initializeForEdit])

  const handleRetry = () => {
    if (saleId) {
      // Reload the page to reset the component state
      window.location.reload()
    }
  }

  // Loading state
  if (loading) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-gray-600">Loading sale data...</span>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center">
              <svg
                className="h-6 w-6 text-red-400 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Failed to load sale
                </h3>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
            <div className="mt-4 space-x-3">
              <button
                onClick={handleRetry}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => void navigate("/birddog")}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Main edit form
  return (
    <SaleForm
      pageTitle="Edit Sale"
      pageDescription="Modify the details of your timber sale"
      finalActionLabel="Update Sale"
      localStorageKey={`sale-edit-draft-${saleId}`}
    />
  )
}
